import { useEffect, useState, useRef, ChangeEvent } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from '@reach/router';
import { QuoteWizardState, Dispatch } from '../../../store';
import {
  setSaveButtonState,
  setAdditionalInformation,
  updateErrors,
} from '../../../store/QuoteWizardState';
import { useQuoteSubmission } from '../../../hooks/useQuoteSubmission';
import { CompleteQuoteSubmissionError } from '../ErrorHandling';
import { BackButton } from '../NavigationButtons';
import { QuestionAnswerDisplay } from '../../common/QuestionAnswerDisplay';
import { hybridAnswers } from '../../util/Quote';
import { getLabel } from './utility';
import { BounceButton } from '../../../Shared';
import { sortReviewForms } from './utility';
import {
  answeredFormMatchesQuoteContext,
  isQuoteSubmissionInProgress,
} from '../utility';
import { QuoteSubmissionProgress } from './QuoteSubmissionProgress/QuoteSubmissionProgress';

import { useIsManager, useWarnBeforeUnloadWithAnswers, useUsingAxisSettings } from '../../../hooks';
import { useScrollToTopOnInitialMount } from '@calefy-inc/hooks';
import { Signature } from './Signature';
import { styled } from '@mui/material';
import { axisPaths } from '../../Axis/AxisConstants';

import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import { UnifiedCompletedForm } from '../../../Typescript/classes';
import { QuoteWizardAnsweredForm, QuoteWizardForm } from '../classes';
import { PolicySummary } from '../types';
import type { BusinessType } from '../../../Typescript';
import { BusinessTypeAlias } from '../../../Typescript/classes';
import type { QuoteStatus } from '../../../Typescript/backend/types';
import { StoreState } from '../../../store';
import { RouteComponentProps } from '@reach/router';
import { StepWizardProps } from 'react-step-wizard';
import { FormEditButton } from './FormEditButton';
import { useWalnutGoogleAnalytics } from '../../../hooks';
import {
  GAEventNames,
  EventActions,
} from '../../../hooks/useWalnutGoogleAnalytics';
import {
  createPageLoadThunk,
  createApplicationFinishedThunk,
} from '../../../store/analyticsStore';
import { DisplayFormErrors } from './FormErrorsDisplay';
import { QuoteWizardQuoteHasNextEditionWarning } from '../QuoteForms/QuoteWizardQuoteHasNextEditionWarning';
import { errorify } from '../../../util';

// Delay between submitting the quote and starting to display the explicit progress
// const VAGO_SUBMISSION_TIMEOUT_MS = 5;
const QUOTE_PROGRESS_DISPLAY_DELAY_MS =
  process.env.NODE_ENV === 'development' ? 0 : 5 * 1000;

interface QuoteReviewDisplayProps
  extends ReturnType<typeof mergeProps>,
    RouteComponentProps,
    StepWizardProps {
  backLink?: string;
  componentMapping?: Record<string, number>;
}

/**
 * Shows the user all of the questions and answers for all of the forms that they've filled out as part of the Quote submission process and allows them to submit them to the backend
 *
 * @param formsWithAnswers - The forms with accompanying user-entered answers
 * @param onConfirmQuote - function fired off to submit the quote (see mapStateToProps and mappedDispatchToProps)
 * @param submittedQuoteForBusinessType - the actual quote which has been submitted; will be undefined until the quote is actually submitted. THIS SHOULD BE A BOOLEAN
 * @param currentQuoteUUID - The UUID of the current quote. Null -> the quote was initialized and completed all in one go, String: -> this quote has been resumed at some point
 * @param allRelevantForms - All of the forms which apply to this quote (not the ones for the policies that were not selected)
 * @param errorPresent - whether or not there is an error somewhere - used to halt submission
 */
export function QuoteReviewDisplay({
  formsWithAnswers,
  onConfirmQuote,
  selectedBusinessType,
  submissionInProgress,
  submittedQuoteForBusinessType,
  allRelevantForms,
  errorPresent,
  // @ts-expect-error - StepWizardProps types incorrectly as any
  currentStep,
  // @ts-expect-error - StepWizardProps types incorrectly as any
  nextStep,
  // @ts-expect-error - StepWizardProps types incorrectly as any
  previousStep,
  componentMapping,
  // @ts-expect-error - StepWizardProps types incorrectly as any
  goToStep,
  // @ts-expect-error - StepWizardProps types incorrectly as any
  totalSteps,
  // @ts-expect-error - StepWizardProps types incorrectly as any
  isActive,
  additionalInformation,
  confirmationInProgress,
}: QuoteReviewDisplayProps) {
  useWarnBeforeUnloadWithAnswers();
  useScrollToTopOnInitialMount();

  const dispatch = useDispatch();
  const { isManager } = useIsManager();
  const navigate = useNavigate();
  const usingAxis = useUsingAxisSettings();
  const [okToContinue, setOkToContinue] = useState<boolean>(false);
  const [okToSubmit, setOkToSubmit] = useState<boolean>(false);
  const [waitingToSubmit, setWaitingToSubmit] = useState<boolean>(false);
  const [displayQuoteProgress, setDisplayQuoteProgress] =
    useState<boolean>(false);

  const progressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { taskPool, quote } = useQuoteSubmission({
    active: waitingToSubmit || !!submissionInProgress,
    onComplete: () => {
      dispatch(createApplicationFinishedThunk());
    },
  });

  const [formsWithAllQuestionsAnswered, setFormsWithAllQuestionsAnswered] =
    useState<Array<UnifiedCompletedForm> | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<null | Error>(null);

  // are there any errors in any of the forms
  const [errorsExist, setErrorsExist] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<{
    [formId: string]: boolean;
  }>({});

  // is the submit button disabled
  const [submitButtonDisabled, setSubmitButtonDisabled] =
    useState<boolean>(true);
  const googleAnalytics = useWalnutGoogleAnalytics();
  useEffect(() => {
    if (!isActive || !googleAnalytics) {
      return;
    }
    googleAnalytics.triggerEvent(
      GAEventNames.Review,
      EventActions.PageLoad,
      '',
    );
  }, [googleAnalytics, isActive]);

  // send the analytics call as soon as we load in
  useEffect(() => {
    const pageLoadData = {
      page: 'Review' as const,
      pageNumber: currentStep,
      totalPages: totalSteps,
    };
    dispatch(createPageLoadThunk(pageLoadData));
  }, [currentStep, totalSteps, dispatch]);

  useEffect(() => {
    dispatch(
      setSaveButtonState({
        visible: true,
        reconcileState: () => {},
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!formsWithAnswers || !allRelevantForms) {
      return;
    }
    try {
      console.time('amendedForms');
      const amendedForms = hybridAnswers(formsWithAnswers, allRelevantForms);
      setFormsWithAllQuestionsAnswered(amendedForms);
      console.timeEnd('amendedForms');
    } catch (e) {
      console.error('Error amending form with default answers:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formsWithAnswers, allRelevantForms]);

  // set OK to continue
  useEffect(() => {
    if (submittedQuoteForBusinessType) {
      setOkToContinue(true);
    } else {
      setOkToContinue(false);
    }
  }, [submittedQuoteForBusinessType]);

  // advance to the done step once submission completes
  useEffect(() => {
    if (!okToContinue) {
      return;
    }

    setDisplayQuoteProgress(false);

    if (usingAxis) {
      navigate(axisPaths.thanks.url);
      return;
    }

    const doneStep = componentMapping?.['done'];
    if (goToStep && doneStep) {
      goToStep(doneStep);
    } else {
      nextStep();
    }
  }, [okToContinue, goToStep, componentMapping, nextStep, usingAxis, navigate]);

  // begin the submission process if it is OK to do so
  useEffect(() => {
    const formsToSubmit = formsWithAllQuestionsAnswered || formsWithAnswers;
    if (okToSubmit) {
      onConfirmQuote(formsToSubmit, signatureData, additionalInformation);
      setWaitingToSubmit(false);

      // Set timeout to display quote progress after submission
      progressTimeoutRef.current = setTimeout(() => {
        setDisplayQuoteProgress(true);
      }, QUOTE_PROGRESS_DISPLAY_DELAY_MS);
    }
  }, [
    okToSubmit,
    formsWithAllQuestionsAnswered,
    formsWithAnswers,
    onConfirmQuote,
    signatureData,
    additionalInformation,
  ]);

  // set whether it is OK to submit the current quote
  useEffect(() => {
    if (
      waitingToSubmit &&
      !errorsExist &&
      !errorPresent &&
      (signatureData || !!signatureError)
    ) {
      setOkToSubmit(true);
    } else {
      setOkToSubmit(false);
    }
  }, [
    waitingToSubmit,
    errorPresent,
    signatureData,
    signatureError,
    errorsExist,
  ]);

  // cancel the current submission if there is an error
  useEffect(() => {
    if (errorPresent || errorsExist) {
      setWaitingToSubmit(false);
    }
  }, [errorPresent, errorsExist]);

  // should the submit button be disabled
  useEffect(() => {
    const disabled =
      !!(submissionInProgress || submittedQuoteForBusinessType) ||
      errorsExist ||
      errorPresent ||
      waitingToSubmit ||
      !(signatureData || !!signatureError) ||
      Object.values(formErrors).some((e) => e);

    setSubmitButtonDisabled(disabled);
  }, [
    submissionInProgress,
    submittedQuoteForBusinessType,
    errorsExist,
    errorPresent,
    waitingToSubmit,
    signatureData,
    signatureError,
    formErrors,
  ]);

  // determine whether any errors exist in the forms
  useEffect(() => {
    const formsToDisplay = formsWithAllQuestionsAnswered;
    if (formsToDisplay === null) {
      return;
    }
    const formErrorsExist = formsToDisplay.some(
      (form) => form.errors && form.errors.length > 0,
    );
    setErrorsExist(formErrorsExist);
  }, [formsWithAllQuestionsAnswered, formsWithAnswers]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (progressTimeoutRef.current !== null) {
        window.clearTimeout(progressTimeoutRef.current);
      }
    };
  }, []);

  // Update errors in state when there are errors from quote submission
  useEffect(() => {
    const taskPoolData = taskPool.data?.getTaskPool;
    const error = taskPool.error || taskPoolData?.error || quote.error;
    //console.log({ error });

    if (error) {
      //console.log('About to dispatch error', error);
      dispatch(
        updateErrors({
          completeQuoteSubmission: errorify(error),
        }),
      );
    }
  }, [
    taskPool.error,
    taskPool.data?.getTaskPool?.error,
    quote.error,
    dispatch,
  ]);

  const formsToDisplay = formsWithAllQuestionsAnswered || [];
  // Use forms to render answers.
  const general = formsToDisplay.filter(
    (formAnswer) => formAnswer.form.policy === null,
  );

  const policies = formsToDisplay.filter(
    (formAnswer) => formAnswer.form.policy !== null,
  );

  const business = selectedBusinessType
    ? selectedBusinessType.displayName
    : null;
  const size = formsToDisplay ? formsToDisplay.length : 0;

  if (displayQuoteProgress) {
    return (
      <>
        <QuoteSubmissionProgress
          taskPool={taskPool.data?.getTaskPool ?? null}
        />
        <CompleteQuoteSubmissionError />
      </>
    );
  }

  return (
    <>
      <>
        <QuoteWizardQuoteHasNextEditionWarning />
        <QuoteReviewHeader key={0}>
          <Typography style={{ color: 'white' }} variant='h5' key={0}>
            {confirmationInProgress
              ? 'Please review the below information carefully, and submit when you are sure that it is correct.'
              : 'Application Details'}
          </Typography>
          <Typography style={{ color: 'white' }} variant='subtitle1' key={1}>
            Line: {business}
          </Typography>
        </QuoteReviewHeader>

        <QuoteReviewContainer key={1}>
          {sortReviewForms([...general, ...policies]).map(
            (amendedFormWithAnswers, index) => {
              const formErrorPresent =
                (amendedFormWithAnswers.errors &&
                  amendedFormWithAnswers.errors.length > 0) ||
                formErrors[amendedFormWithAnswers.form.id];
              return (
                <>
                  <FormTitleContainer id={amendedFormWithAnswers.form.id}>
                    <Typography
                      variant='h6'
                      color={formErrorPresent ? 'error.main' : 'primary'}
                    >
                      {getLabel(amendedFormWithAnswers)}
                    </Typography>
                    {formErrorPresent ? (
                      <FormErrorWarningContainer>
                        <ErrorOutlineIcon color='error' />
                        <Typography color='error'>
                          {'This form has an error.'}
                        </Typography>
                      </FormErrorWarningContainer>
                    ) : null}
                    <FormEditButton
                      policy={amendedFormWithAnswers.form.policy}
                      internalName={amendedFormWithAnswers.form.internalName}
                      setFormErrorPresent={(b: boolean) => {
                        setFormErrors((oldErrors) => {
                          return {
                            ...oldErrors,
                            [amendedFormWithAnswers.form.id]: b,
                          };
                        });
                      }}
                    />
                  </FormTitleContainer>
                  <AnswerContentsList>
                    {amendedFormWithAnswers.answers.map(
                      (answer, index, answers) => {
                        return (
                          <QuestionAnswerDisplay
                            form={amendedFormWithAnswers}
                            topLevelAnswer={answer}
                            edit={true}
                            answer={answer}
                            answers={answers}
                            indent={0}
                            key={answer.apiName + index}
                          />
                        );
                      },
                    )}
                  </AnswerContentsList>
                  <FormDividerContainer>
                    {index + 1 !== size || index + 1 === size ? (
                      <Divider variant='middle' />
                    ) : null}
                  </FormDividerContainer>
                </>
              );
            },
          )}
          <AdditionalInformationContainer>
            <TextField
              rows={5}
              value={additionalInformation}
              onChange={(
                e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => dispatch(setAdditionalInformation(e.target.value))}
              id='additional-information'
              label='Additional Information or Comments'
              fullWidth
              multiline
            />
          </AdditionalInformationContainer>
          <SignatureContainer>
            <Signature
              handleImageData={(imageData: string | null) => {
                setSignatureData(imageData);
              }}
              onError={(error: Error) => {
                setSignatureError(error);
              }}
            />
          </SignatureContainer>
          {!!signatureError ? null : (
            <FormDividerContainer>
              <Divider variant='middle' />
            </FormDividerContainer>
          )}{' '}
          <DisplayFormErrorsContainer>
            <DisplayFormErrors formErrors={formErrors} />
          </DisplayFormErrorsContainer>
          <QuoteWizardQuoteHasNextEditionWarning />
          <SubmitButtonContainer>
            <CompleteQuoteSubmissionError />
            <BounceButton
              variant='contained'
              color={!isManager ? 'secondary' : 'primary'}
              size='large'
              disabled={submitButtonDisabled}
              onClick={() => {
                setWaitingToSubmit(true);
              }}
            >
              {(waitingToSubmit || submissionInProgress) && !okToContinue ? (
                <>
                  Submitting... <SubmittingSpinner size='1rem' />
                </>
              ) : (
                `${
                  confirmationInProgress ? 'Confirm and ' : ''
                }Submit all forms`
              )}
            </BounceButton>
          </SubmitButtonContainer>
          <NavigationButtonsContainer>
            <BackButton onClick={previousStep} type='button' />
          </NavigationButtonsContainer>
        </QuoteReviewContainer>
        <div id='google-attribution'></div>
      </>
    </>
  );
}

const DisplayFormErrorsContainer = styled('div')(({ theme }) => {
  return {
    padding: theme.spacing(1, 0),
  };
});

/* Connect Layer */

function findSubmittedQuoteForBusinessType(
  submittedQuotes: StoreState['quoteWizard']['submittedQuotes'],
  selectedBusinessType: StoreState['quoteWizard']['selectedBusinessType'],
) {
  const submittedQuotesExcludingContactInfoOnly = submittedQuotes.filter(
    (quote) => quote.status !== 'CONTACT_INFO_ONLY',
  );

  if (submittedQuotesExcludingContactInfoOnly.length === 0) {
    return undefined;
  }

  const selectedBusinessTypeId = !selectedBusinessType
    ? undefined
    : 'id' in selectedBusinessType
    ? selectedBusinessType.id
    : selectedBusinessType?.original.id;

  if (selectedBusinessTypeId) {
    return submittedQuotesExcludingContactInfoOnly.find(
      (quote) => quote.businessLine.id === selectedBusinessTypeId,
    );
  }

  return submittedQuotesExcludingContactInfoOnly[
    submittedQuotesExcludingContactInfoOnly.length - 1
  ];
}

function isQuoteWizardAnsweredFormArray(
  forms: Array<UnifiedCompletedForm> | Array<QuoteWizardAnsweredForm>,
): forms is Array<QuoteWizardAnsweredForm> {
  return (
    forms.length > 0 &&
    'toUnifiedCompletedForm' in forms[0] &&
    typeof forms[0].toUnifiedCompletedForm === 'function'
  );
}

function mapStateToProps(state: StoreState) {
  const answersByFormId = state.quoteWizard.formAnswers;
  const selectedBusinessType = state.quoteWizard.selectedBusinessType;

  const allSelectedPolicyIds = state.quoteWizard.selectedPolicies.map(
    (fullPolicy: PolicySummary) => fullPolicy.id,
  );
  const allRelevantForms = [
    state.quoteWizard.businessForm,
    ...state.quoteWizard.policyForms,
  ].filter((form): form is QuoteWizardForm => {
    if (!QuoteWizardForm.isQuoteWizardForm(form)) {
      return false;
    }
    return (
      !form.policy ||
      !!(form.policy?.id && allSelectedPolicyIds.includes(form.policy.id))
    );
  });

  const formsWithAnswers: Array<QuoteWizardAnsweredForm> = Object.values(
    answersByFormId,
  ).filter((answeredForm) =>
    answeredFormMatchesQuoteContext(
      answeredForm,
      selectedBusinessType,
      allSelectedPolicyIds,
    ),
  );

  const additionalInformation = state.quoteWizard.additionalInformation;

  const submissionInProgress = isQuoteSubmissionInProgress(
    formsWithAnswers,
    state.quoteWizard.quoteSubmissionTaskPoolId,
  );

  const submittedQuoteForBusinessType = findSubmittedQuoteForBusinessType(
    state.quoteWizard.submittedQuotes,
    selectedBusinessType,
  );

  const errorPresent = Object.values(state.quoteWizard.errors).some(
    (errorStatus) => errorStatus !== null,
  );

  return {
    formsWithAnswers,
    selectedBusinessType,
    currentQuoteUUID: state.quoteWizard.currentQuoteUUID,
    submissionInProgress,
    submittedQuoteForBusinessType,
    allRelevantForms,
    errorPresent,
    renewalInProgress: state.quoteWizard.renewalInProgress,
    additionalInformation,
    confirmationInProgress: state.quoteWizard.confirmationInProgress,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  const { confirmAnswersForForms } = QuoteWizardState.actions;

  function onConfirmQuote({
    formsWithAnswers,
    businessType,
    status,
    currentQuoteUUID,
    signature,
    additionalInformation,
    renewalInProgress,
  }: {
    formsWithAnswers: Array<UnifiedCompletedForm>;
    businessType?: BusinessType | BusinessTypeAlias;
    status?: QuoteStatus;
    currentQuoteUUID: string | null;
    signature: string | null;
    additionalInformation?: string;
    renewalInProgress: boolean;
  }) {
    const resolvedBusinessType: BusinessType | undefined = businessType
      ? 'original' in businessType
        ? businessType.original
        : businessType
      : undefined;

    return dispatch(
      confirmAnswersForForms({
        businessType: resolvedBusinessType,
        formsWithAnswers,
        status,
        currentQuoteUUID,
        signature,
        additionalInformation,
        renewalInProgress,
        selectedPolicies: [],
        storeExistingQuoteUuidOnComplete: false,
      }),
    );
  }

  return {
    onConfirmQuote,
  };
}

function mergeProps(
  mappedState: ReturnType<typeof mapStateToProps>,
  mappedDispatch: ReturnType<typeof mapDispatchToProps>,
  ownProps: Record<string, unknown>,
) {
  const onConfirmQuote = (
    amendedFormsWithAnswers:
      | Array<UnifiedCompletedForm>
      | Array<QuoteWizardAnsweredForm>,
    signature: string | null,
    additionalInformation: string,
  ) => {
    const formsToSubmit: Array<UnifiedCompletedForm> =
      isQuoteWizardAnsweredFormArray(amendedFormsWithAnswers)
        ? amendedFormsWithAnswers.map((form) => form.toUnifiedCompletedForm())
        : amendedFormsWithAnswers;

    return mappedDispatch.onConfirmQuote({
      businessType: mappedState.selectedBusinessType || undefined,
      formsWithAnswers: formsToSubmit,
      status: mappedState.renewalInProgress
        ? 'RENEWED'
        : mappedState.confirmationInProgress
        ? 'CONFIRMED'
        : 'COMPLETE',
      currentQuoteUUID: mappedState.currentQuoteUUID,
      signature,
      additionalInformation,
      renewalInProgress: mappedState.renewalInProgress,
    });
  };

  return {
    ...mappedState,
    ...mappedDispatch,
    ...ownProps,
    onConfirmQuote,
  };
}

export const QuoteReviewConnectLayer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
);

export default QuoteReviewConnectLayer((mappedState: $TSFixMe) => (
  <QuoteReviewDisplay {...mappedState} />
));

// ----------------------------------------------------------------------
// Styled Components
// ----------------------------------------------------------------------

const QuoteReviewHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  margin: 'auto',
  flexDirection: 'column',
  maxWidth: '60rem',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[700],
}));

const QuoteReviewContainer = styled('div')(({ theme }) => ({
  backgroundColor: '#DFE3E8',
  display: 'flex',
  margin: 'auto',
  marginBottom: theme.spacing(4),
  flexDirection: 'column',
  padding: `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(2)}`,
  maxWidth: '60rem',
}));

const FormTitleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const AnswerContentsList = styled('dl')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '[question] 2fr [answer] 2fr',
  '& dt': {
    display: 'inline',
    ...theme.typography.body2,
  },
  '& dd': {
    display: 'inline-flex',
    flexDirection: 'row',
    ...theme.typography.body2,
    textAlign: 'left',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  '& dd:after': {
    content: '" "',
    display: 'block',
  },
}));

const FormDividerContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const AdditionalInformationContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const SignatureContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const SubmitButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
});

const SubmittingSpinner = styled(CircularProgress)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const NavigationButtonsContainer = styled('div')({});

const FormErrorWarningContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
  };
});
